// TALKING TO THE UNIVERSAL INPUT ABOUT THE OPEN EMAIL (busy-my-web#524).
//
// Fatemeh's 2026-09-16 frames 40011626:363587 → 40011640:369482. An email is open in the reader and
// the box under it prompts "Ask about this email...."; a question typed there does NOT leave for
// Home the way every other ask did (lib/ongoingChat.js's `home-chat`) — the middle turns into the
// CONVERSATION about this mail, the gutter's two rows collapse to a single "✕ Close"
// (lib/boxChrome.js), the breadcrumb grows a "Conversation ✕" chip, and the Email timeline rail
// gains a second node saying the conversation started. Closing it puts the mail back with a new
// "💬 Conversation" row in the box's briefing header, which reopens what was said.
//
// Nothing here is new machinery: the turns themselves are the app's ordinary chat
// (store/modules/timelineSearch.js, drawn by components/Home/AiAnswer.vue) asked with the scope
// lib/askScope.js already writes for an open thread — `thread:<timelineId>` (busy-main-bff#20). No
// BFF field was invented for this; the ask is the same `ai/ask` body every other surface sends.
//
// This module is the pure half: what the conversation is CALLED, what the crumb says, what the
// briefing row and the rail node say, and which prompt the composer wears. Pure so that the state
// machine can be checked without a mailbox, a router or a mounted box behind it.

/** Every word the frames put on screen for this journey, in one place. */
export const EMAIL_CONVERSATION_TEXT = Object.freeze({
  /** the composer before a conversation exists (frame 40011626:363587) — #517's own spelling */
  ask: 'Ask about this email....',
  /** …and over an open Briefing ENTRY's listing — "1,051 emails", "26 need action" (busy-my-web#615,
   *  Saffet 2026-10-09, Slack p1791535225319699) — where the box is about many emails, not one */
  entryAsk: 'Ask Busy anything about these emails…',
  /** …and once the conversation is open (frame 40011642:370063) */
  followUp: 'Ask a follow-up',
  /** the active breadcrumb chip, and the briefing header row that reopens it */
  crumb: 'Conversation',
  /** the Email timeline rail's second node (the same words lib/chatRail.js uses for a chat) */
  rail: 'Conversation started',
  /** what a conversation with no derivable name is called */
  untitled: 'Conversation',
})

/** The chat glyph the frames draw beside the crumb, the briefing row and the rail node. */
export const EMAIL_CONVERSATION_ICON = 'message-text'

// Mailbox role words. A company writes to you as "HR Khorasan Gostar" or "Coursera Support", and
// the conversation in frame 40011642:370063 is titled "Khorasan Gostar Company" — the ORGANISATION,
// not the desk inside it. These are stripped from either end before the name is read.
const ROLE_WORDS = new Set([
  'hr', 'support', 'team', 'info', 'admin', 'sales', 'careers', 'career', 'recruitment',
  'recruiting', 'hello', 'contact', 'help', 'billing', 'accounts', 'notifications', 'noreply',
  'no-reply', 'donotreply', 'do-not-reply', 'service', 'customer', 'office', 'the',
])

// …and the suffixes that already say "an organisation", so the derived title does not end up as
// "Acme Ltd Company".
const ORG_SUFFIXES = new Set([
  'company', 'co', 'co.', 'ltd', 'ltd.', 'limited', 'inc', 'inc.', 'llc', 'llp', 'plc', 'gmbh',
  'corp', 'corp.', 'corporation', 'group', 'holdings', 'sa', 'ag', 'bv', 'nv', 'srl', 'oy', 'ab',
])

/**
 * The conversation's NAME, derived from the sender's display name.
 *
 * DECISION (2026-09-17): bff#42's AI-written chat-session title is in Test and not on this branch,
 * so the title is derived here exactly as the spec asked — "<Sender org>" out of the sender display
 * name — and the derivation is: drop the role words at either end ("HR Khorasan Gostar" →
 * "Khorasan Gostar"), then say it is a company unless the name already does ("Acme Ltd" stays).
 * When #42 lands, a server title simply wins and this becomes the fallback: `conversationTitle`
 * takes the given name first for exactly that reason.
 *
 * @param {string} senderName  the sender's display name (lib/senderDisplay's `senderDisplayName`)
 * @param {string} given       a name the server (or the user) already gave the session
 */
export function conversationTitle(senderName, given = '') {
  const own = String(given || '').replace(/\s+/g, ' ').trim()
  if (own) return own

  const words = String(senderName || '').replace(/[<>"]/g, ' ').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean)
  // An address rather than a name ("hr@khorasangostar.com") carries no organisation we may read
  // without guessing at the domain, so it is left alone as the name it is.
  while (words.length && ROLE_WORDS.has(words[0].toLowerCase())) words.shift()
  while (words.length && ROLE_WORDS.has(words[words.length - 1].toLowerCase())) words.pop()
  if (!words.length) return EMAIL_CONVERSATION_TEXT.untitled

  const last = words[words.length - 1].toLowerCase().replace(/[,]$/, '')
  if (ORG_SUFFIXES.has(last)) return words.join(' ')
  return `${words.join(' ')} Company`
}

/**
 * The breadcrumb over the composer: "Briefing . Khoras… > [Conversation ✕]".
 *
 * The first crumb is the email's own briefing line, shortened the way the frame shortens it, and
 * the second is the ACTIVE chip — closeable, because closing it is what leaves the conversation.
 *
 * @param {string} subject  the open email's subject
 * @param {number} max      how many characters of the subject the first crumb keeps
 */
export function conversationCrumbs(subject, max = 9) {
  const text = String(subject || '').replace(/\s+/g, ' ').trim()
  const limit = Math.max(1, Number(max) || 1)
  // Trimmed before the ellipsis: a cut that lands on a space would otherwise read "Khorasan …".
  const short = text.length > limit ? `${text.slice(0, limit).trimEnd()}…` : text
  return [
    { key: 'email', label: short ? `Briefing . ${short}` : 'Briefing', current: false, closeable: false },
    { key: 'conversation', label: EMAIL_CONVERSATION_TEXT.crumb, current: true, closeable: true },
  ]
}

/**
 * Which prompt the composer wears while the box is about an email.
 *
 * Null when the box is about neither — the caller then falls through to its own ladder of prompts
 * rather than this module deciding for surfaces it knows nothing about.
 */
export function conversationPlaceholder({ readerOpen = false, conversationOpen = false, entryOpen = false } = {}) {
  if (conversationOpen) return EMAIL_CONVERSATION_TEXT.followUp
  if (readerOpen) return EMAIL_CONVERSATION_TEXT.ask
  // An entry level is the LEAST particular of the three: a thread opened out of the listing is one
  // email again, and takes "Ask about this email…" back (busy-my-web#615).
  if (entryOpen) return EMAIL_CONVERSATION_TEXT.entryAsk
  return null
}

/**
 * The "💬 Conversation" row the box's briefing header grows once this email HAS a conversation
 * (frame 40011640:369482 — it sits above "◎ 1 possible ongoing match"). Null while there is none:
 * a briefing states what is there, and an empty row is a door to nothing.
 */
export function conversationBriefingRow(conversation) {
  if (!hasConversation(conversation)) return null
  return {
    key: 'conversation',
    icon: EMAIL_CONVERSATION_ICON,
    label: EMAIL_CONVERSATION_TEXT.crumb,
    action: 'open-conversation',
  }
}

/**
 * The rail's second node — "Could you find the website … / Conversation started" (frame
 * 40011642:370063). Built from the chat that is already in the store, so nothing new is asked of
 * any service: the rail is a client-side reading of the conversation, exactly as lib/chatRail.js is
 * for the Home chat.
 *
 * `title` is the FIRST question, which is what started the conversation — the same choice
 * `chatRailItem` makes, and for the same reason.
 */
export function conversationRailNode(conversation) {
  // `hasConversation` is the one guard: it is true exactly when there is a turn with a question in
  // it, which is the turn this node is built from — so there is no second emptiness to check for.
  if (!hasConversation(conversation)) return null
  const first = conversation.turns.find(t => t && String(t.question || '').trim())
  return {
    key: `conversation:${conversation.threadId || 'thread'}`,
    kind: 'conversation',
    label: EMAIL_CONVERSATION_TEXT.rail,
    subject: String(first.question).replace(/\s+/g, ' ').trim(),
    icon: EMAIL_CONVERSATION_ICON,
    color: '#3741f3',
    at: first.askedUtc || first.createdUtc || conversation.startedUtc || null,
  }
}

/** Is there a conversation on this email at all? One turn with a question in it is enough. */
export function hasConversation(conversation) {
  const turns = conversation?.turns
  return Array.isArray(turns) && turns.some(t => t && String(t.question || '').trim())
}

/**
 * The scope an email conversation is asked in — `thread:<timelineId>`, which is the string
 * lib/askScope.js already writes for an open thread and busy-main-bff#20 already accepts. Stated
 * here so the reader's ask cannot drift from the route's.
 */
export function conversationScope(threadId) {
  const id = String(threadId || '').trim()
  return id ? `thread:${id}` : 'home'
}

export default conversationTitle
